import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Mail } from "lucide-react";
import { api, savedInvite } from "@/lib/api";
import { useGuest, countWord } from "@/lib/GuestContext";
import Countdown from "@/components/Countdown";
import Poster from "@/components/Poster";
import { inviteHref } from "@/components/Nav";
import { useReveal } from "@/lib/useReveal";
import { Asterisk, Heart, Squiggle, Stamp, ClockFace, PhotoFrame, Marquee, Ticket, Mug, useInView } from "@/components/Motifs";

export default function Home() {
  const [s, setS] = useState({});
  const [events, setEvents] = useState([]);
  const { guest } = useGuest();
  useEffect(() => {
    api.get("/public/settings").then((r) => setS(r.data || {}));
    api.get("/public/events").then((r) => setEvents(r.data || []));
  }, [guest]);
  const photos = s.couple_photos || {};
  return (
    <div data-testid="home-page">
      <Hero photos={photos} guest={guest} events={events} />
      <Marquee className="bg-[#A62D58] text-[#FFF9F0] border-y-2 border-[#252122]" items={["It's happening!!", "#MaamlaNiGiHai", "11:11", "03.12.2026", "Mumbai", "Some stories are worth waiting for"]} />
      <StorySoFar photos={photos} />
      <Timing />
      {guest && events.length > 0 && <CelebrationGrid events={events} />}
      <CountdownBand target={s.wedding_date || "2026-12-03T11:11:00+05:30"} />
      <InvitationBand />
    </div>
  );
}

function Hero({ photos, guest, events }) {
  const single = events.length === 1;
  return (
    <section data-testid="hero-section" className="grain relative overflow-hidden bg-[#FFF9F0]">
      <div className="absolute -right-32 top-10 w-[520px] h-[520px] rounded-full bg-[#F7C9D0] hidden md:block" />
      <div className="absolute right-[18%] bottom-[-120px] w-[300px] h-[420px] rounded-t-full bg-[#F7B733] hidden lg:block" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-8 md:pt-14 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div className="rise flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 bg-[#252122] text-[#F7B733] rounded-full px-4 py-2 kicker -rotate-2"><Asterisk size={12} /> 11:11 | The art of timing</span>
            {guest && <span data-testid="hero-welcome" className="font-hand text-xl text-[#A62D58] rotate-1">welcome, {guest.family_name}!</span>}
          </div>
          <p className="rise d1 font-display italic text-[#FF6657] text-5xl md:text-7xl mt-6 leading-none" data-testid="hero-kicker">It's happening!!</p>
          <h1 data-testid="hero-title" className="rise d2 font-display font-medium text-[22vw] sm:text-[8rem] lg:text-[9.5rem] leading-[0.8] tracking-[-0.05em] mt-4 text-[#252122]">
            Niyati <span className="italic text-[#A62D58]">&amp;</span><br />Girish
          </h1>
          <div className="rise d3 mt-7 flex items-start gap-2 text-[#3458B5]">
            <Squiggle width={80} className="mt-2 rotate-[8deg]" />
            <p className="font-hand text-2xl md:text-3xl leading-tight max-w-md">Plot twist: the best friends are getting married.</p>
          </div>
          <div className="rise d3 mt-8 flex flex-wrap gap-4">
            <Ticket className="bg-[#252122] text-[#FFF9F0]">03.12.2026</Ticket>
            <Ticket className="bg-[#3458B5] text-[#FFF9F0]">Mumbai</Ticket>
          </div>
          <div className="rise d4 mt-10 flex flex-col sm:flex-row gap-4">
            {guest && events.length > 0 ? (
              <Link to={single ? `/celebrations/${events[0].slug}` : "/celebrations"} data-testid="hero-cta-celebrations" className="btn-pop bg-[#FF6657] text-[#252122] rounded-full px-7 py-4 kicker inline-flex items-center justify-center gap-2">
                {single ? "See the celebration" : "Let the celebrations begin"} <ArrowUpRight size={16} />
              </Link>
            ) : (
              <Link to="/story" data-testid="hero-cta-story" className="btn-pop bg-[#FF6657] text-[#252122] rounded-full px-7 py-4 kicker inline-flex items-center justify-center gap-2">
                Read our story <ArrowUpRight size={16} />
              </Link>
            )}
            <Link to={inviteHref()} data-testid="hero-cta-invite" className="btn-pop bg-[#FFF9F0] text-[#252122] rounded-full px-7 py-4 kicker inline-flex items-center justify-center gap-2">
              <Mail size={15} /> Open your invitation
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 relative h-[440px] sm:h-[520px] md:h-[600px] w-full max-w-[480px] mx-auto">
          <PhotoFrame testid="hero-photo-1" src={photos.hero_1} initial="N&G" tone="#F7C9D0" rotate={-5} caption="the main characters" className="absolute left-[2%] top-0 w-[62%] z-[2]" />
          <PhotoFrame testid="hero-photo-2" src={photos.hero_2} initial="11:11" tone="#A9C79D" rotate={7} caption="impeccable timing" className="absolute right-0 bottom-0 w-[50%] z-[3]" ratio="aspect-square" />
          <Stamp className="stamp-in absolute -left-4 top-[46%] w-28 h-28 md:w-36 md:h-36 z-[4] bg-[#FFF9F0] rounded-full shadow-lg" />
          <Heart className="absolute right-6 top-4 text-[#FF6657] -rotate-12 z-[4] floaty" size={56} />
          <Mug className="absolute right-[42%] bottom-[-6px] w-16 z-[1] text-[#252122] hidden sm:block -rotate-6" />
        </div>
      </div>
    </section>
  );
}

function StorySoFar({ photos }) {
  const r = useReveal();
  return (
    <section className="bg-[#F7C9D0] overflow-hidden">
      <div ref={r} className="reveal max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6 relative h-[420px] md:h-[520px] w-full max-w-[520px] mx-auto order-2 lg:order-1">
          <PhotoFrame src={photos.story_1} initial="EY" tone="#FFF9F0" rotate={-8} caption="where it all began" className="absolute left-0 top-6 w-[52%]" />
          <PhotoFrame src={photos.story_2} initial="BFF" tone="#F7B733" rotate={5} caption="the best-friend era" className="absolute left-[34%] top-0 w-[46%] z-[2]" ratio="aspect-square" />
          <PhotoFrame src={photos.story_3} initial="♡" tone="#A9C79D" rotate={-2} caption="the plot twist" className="absolute left-[18%] bottom-0 w-[50%] z-[3]" ratio="aspect-[5/4]" />
          <span className="absolute right-2 bottom-16 font-hand text-xl text-[#A62D58] rotate-[-8deg] z-[4] hidden sm:block">(narrator: they were<br />not just colleagues)</span>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="kicker text-[#A62D58]">Previously, on NiGi</div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.92] mt-4 text-[#252122]">From colleagues to <span className="italic text-[#A62D58]">co-conspirators.</span></h2>
          <p className="font-hand text-2xl md:text-3xl mt-6 text-[#252122]/80 max-w-lg">"Somewhere between meetings, friendship and impeccable timing, this happened."</p>
          <Link to="/story" data-testid="story-cta" className="btn-pop mt-10 inline-flex items-center gap-2 bg-[#252122] text-[#FFF9F0] rounded-full px-7 py-4 kicker" style={{ boxShadow: "4px 4px 0 #A62D58" }}>
            Binge the full story <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Timing() {
  const [ref, on] = useInView(0.35);
  return (
    <section ref={ref} data-testid="timing-section" className="bg-[#21191E] text-[#FFF9F0] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 flex justify-center">
          <ClockFace active={on} className="w-64 h-64 md:w-[22rem] md:h-[22rem]" />
        </div>
        <div className="md:col-span-7">
          <div className="kicker text-[#C6A46C]">Our signature</div>
          <div className="font-display text-[30vw] md:text-[13rem] leading-[0.8] tracking-[-0.06em] text-[#C6A46C] mt-2 flex">
            {"11:11".split("").map((c, i) => (
              <span key={i} className={on ? "rise" : "opacity-0"} style={{ animationDelay: `${0.4 + i * 0.12}s` }}>{c}</span>
            ))}
          </div>
          <p className="font-display text-3xl md:text-5xl leading-tight mt-6">Some call it coincidence.<br /><span className="italic text-[#F7C9D0]">We call it impeccable timing.</span></p>
          <p className="font-hand text-xl text-[#F7B733] mt-6 flex items-center gap-2"><Squiggle width={60} className="-scale-x-100" /> go on, make a wish</p>
        </div>
      </div>
    </section>
  );
}

function CelebrationGrid({ events }) {
  const spans = ["md:col-span-7", "md:col-span-5 md:mt-20", "md:col-span-5 md:-mt-16", "md:col-span-7"];
  const n = events.length;
  return (
    <section className="bg-[#FFF9F0] py-20 md:py-28 overflow-hidden" data-testid="celebrations-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="kicker text-[#FF6657]">{n === 1 ? "The celebration" : "Our celebrations"}</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.92] mt-3" data-testid="celebrations-heading">
              {n === 1 ? <>One celebration.<br /><span className="italic">Your seat</span> is saved.</> : <>{countWord(n)} celebrations.<br /><span className="italic">Zero</span> chill.</>}
            </h2>
          </div>
          <Link to={n === 1 ? `/celebrations/${events[0].slug}` : "/celebrations"} className="kicker link-sweep text-[#A62D58]">{n === 1 ? "See the details →" : "See the full itinerary →"}</Link>
        </div>
        <div className={`flex md:grid md:grid-cols-12 md:items-start gap-5 md:gap-8 overflow-x-auto md:overflow-visible snap-x snap-mandatory no-scrollbar -mx-5 px-5 md:mx-0 md:px-0 pb-4 ${n === 1 ? "md:max-w-3xl md:mx-auto" : ""}`}>
          {events.map((e, i) => (
            <div key={e.slug} className={`shrink-0 w-[84vw] sm:w-[60vw] md:w-auto snap-center flex ${n === 1 ? "md:col-span-12" : spans[i % 4]}`}>
              <Poster e={e} i={i} className="w-full" />
            </div>
          ))}
        </div>
        {n > 1 && <div className="md:hidden font-hand text-lg text-[#252122]/60 mt-3">swipe for more →</div>}
      </div>
    </section>
  );
}

function CountdownBand({ target }) {
  return (
    <section className="bg-[#FF6657] text-[#252122] border-y-2 border-[#252122] relative overflow-hidden">
      <Asterisk size={120} className="absolute -right-6 -top-8 text-[#F7B733] spin-slow hidden md:block" />
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="kicker">The countdown</div>
        <h2 className="font-display italic text-4xl md:text-6xl leading-tight mt-3 mb-12">Until the shaadi shenanigans begin...</h2>
        <Countdown target={target} />
      </div>
    </section>
  );
}

export function InvitationBand() {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  const saved = savedInvite();
  const go = (e) => {
    e.preventDefault();
    const token = code.trim().split(/[?#]/)[0].split("/").filter(Boolean).pop();
    if (token) nav(`/invite/${token}`);
  };
  return (
    <section id="invitation" data-testid="invitation-section" className="bg-[#3458B5] text-[#FFF9F0] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 relative h-[320px] md:h-[380px]">
          <div className="absolute left-[12%] top-0 w-[70%] h-[70%] bg-[#F7C9D0] rotate-[8deg] shadow-xl" />
          <div className="absolute left-[4%] top-[14%] w-[78%] h-[72%] bg-[#FFF9F0] -rotate-3 shadow-2xl p-6 flex flex-col justify-between text-[#252122]">
            <div className="kicker text-[#A62D58]">You are cordially invited</div>
            <div className="font-display italic text-4xl md:text-5xl leading-none">It's<br />happening!!</div>
            <div className="font-hand text-lg">love, N &amp; G</div>
          </div>
          <div className="absolute right-[6%] top-[6%] w-20 h-24 bg-[#F7B733] border-[3px] border-dashed border-[#FFF9F0] rotate-6 flex flex-col items-center justify-center text-[#252122] shadow-lg">
            <span className="font-display text-xl leading-none">11:11</span><span className="kicker text-[8px] mt-1">Mumbai</span>
          </div>
          <div className="absolute right-[18%] bottom-0 w-20 h-20 rounded-full bg-[#A62D58] border-4 border-[#C6A46C] flex items-center justify-center font-display italic text-[#F7C9D0] text-xl shadow-xl">NiGi</div>
        </div>
        <div className="lg:col-span-7">
          <div className="kicker text-[#F7B733]">Your invitation</div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.92] mt-3">Psst... you've got a <span className="italic text-[#F7C9D0]">wedding invitation.</span></h2>
          <p className="font-hand text-2xl md:text-3xl mt-5 opacity-90">Your seat at the celebrations is waiting.</p>
          {saved && (
            <Link to={`/invite/${saved}`} data-testid="invite-welcome-back" className="btn-pop mt-8 inline-flex bg-[#F7B733] text-[#252122] rounded-full px-7 py-4 kicker">Welcome back, open my invitation</Link>
          )}
          <form onSubmit={go} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
            <input data-testid="invite-token-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste your invitation link or code"
              className="flex-1 bg-[#FFF9F0] text-[#252122] rounded-full px-6 py-4 border-2 border-[#252122] outline-none focus:ring-4 focus:ring-[#F7B733]/60 placeholder:text-[#252122]/40" />
            <button data-testid="invite-token-go" type="submit" className="btn-pop bg-[#FF6657] text-[#252122] rounded-full px-7 py-4 kicker">Open it</button>
          </form>
          <p className="text-sm opacity-70 mt-4">Your link arrived on WhatsApp or email. Each invitation is personal to your family.</p>
        </div>
      </div>
    </section>
  );
}
